import { Dashboardv20240509Api } from '@sequencehq/api/dashboard/v20240509'
import { Currency } from '@sequencehq/api'
import {
  v1ApiCreateListPriceResult,
  v1ApiCreateProductResult
} from '../communication/external/v1/ports/list.api.v1/entitySaving'

// TODO: we really need to make this a util somewhere!
export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends object | undefined
      ? RecursivePartial<T[P]>
      : T[P]
}

export type ApiListPrice = Dashboardv20240509Api.GetListPrice.ListPrice
export type ApiPricingTier =
  | Dashboardv20240509Api.CommonModels.PricingStructure.GraduatedPricingTier
  | Dashboardv20240509Api.CommonModels.PricingStructure.VolumeFixedPricingTier
  | Dashboardv20240509Api.CommonModels.PricingStructure.VolumePercentagePricingTier
  | Dashboardv20240509Api.CommonModels.PricingStructure.SeatBasePricingTier

export type RevenueRecognitionMethod =
  | 'MILESTONE'
  | 'STRAIGHT_LINE'
  | 'USAGE'
  | 'POINT_IN_TIME'
export type Product = {
  id: string
  name: string
  label?: string
  taxCategoryId?: string
  taxCategoryName?: string
  revenueRecognitionMethod?: RevenueRecognitionMethod
  deferredRevenueXeroAccountMappingId?: string
  recognizedRevenueXeroAccountMappingId?: string
  unbilledRevenueXeroAccountMappingId?: string
}

export type PricingEditorLedgerIntegration = {
  id: string
  defaultLedgerAccount?: string
  options: {
    id: string
    name: string
    code: string
  }[]
}

type UsageMetricParameter = {
  id: string
  usageMetricId: string
  name: string
  type: 'INTEGER' | 'POSITIVE_INTEGER' | 'NUMBER' | 'POSITIVE_NUMBER'
  description: string
  defaultValue: string
}

export type UsageMetric = {
  id: string
  name: string
  aggregationType: 'COUNT' | 'UNIQUE' | 'SUM' | 'CUSTOM'
  parameters: Array<UsageMetricParameter>
}

export type BillingType = 'IN_ARREARS' | 'IN_ADVANCE'

export const availableBillingTypeOptions: {
  value: BillingType
  label: string
}[] = [
  { label: 'In arrears', value: 'IN_ARREARS' },
  { label: 'In advance', value: 'IN_ADVANCE' }
]

// Mirror of the `PricingModel` keys in `@sequencehq/utils`'
// `PRICING_MODEL_LABELS`. Spelled out explicitly here (rather than
// re-exported as `keyof typeof ...`) so ts-pattern can narrow the
// union literally — `keyof typeof` confused exhaustive matching in
// downstream consumers like `useReviewForm`. Keep this in sync with
// PRICING_MODEL_LABELS; the keys-match assertion below catches drift
// at the type level.
export type PricingModel =
  | 'STANDARD'
  | 'LINEAR'
  | 'VOLUME'
  | 'GRADUATED'
  | 'PACKAGE'
  | 'SEAT_BASED_LINEAR'
  | 'SEAT_BASED_GRADUATED'

// Compile-time drift guard: forces the explicit union above to stay
// mutually exclusive with the `PRICING_MODEL_LABELS` dictionary keys
// in `@sequencehq/utils`. Adding a new model in either place without
// updating the other surfaces here as a type error.
type _AssertPricingModelMatchesLabels = [
  PricingModel extends UtilsPricingModel ? true : never,
  UtilsPricingModel extends PricingModel ? true : never
]
type UtilsPricingModel = import('@sequencehq/utils').PricingModel

type BasePrice<PM extends PricingModel, S> = {
  id: string
  common: {
    pricingModel: PM
    productId: string
    currency: Currency
    name: string
    integrationIds: Record<PricingEditorLedgerIntegration['id'], string>
    listPriceId?: string
  }
  modelSpecific: S
}

export type BillingFrequency =
  | 'ONE_TIME'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'YEARLY'
  | 'HALF_YEARLY'
  | 'ON_DEMAND'
  | 'PER_EVENT'

export type UsageTierType = 'FIXED' | 'PERCENTAGE'

export type LinearPriceType = 'FIXED' | 'PERCENTAGE'

export type UsageCalculationMode = 'BILLING_PERIOD' | 'CUMULATIVE' | 'PERIODIC'

export type UsageCalculationFrequency =
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'HALF_YEARLY'
  | 'YEARLY'

type ProrationStrategy =
  | 'USE_MAXIMUM'
  | 'PRORATE_INCREMENTS'
  | 'PRORATE_ALL_CHANGES'

export type PriceEditorTier = {
  id: string
  firstUnit: string
  lastUnit: string
  unitPrice: string
  flatFee: string
}

export type PriceEditorPercentageTier = {
  id: string
  firstUnit: string
  lastUnit: string
  maxPrice: string
  minPrice: string
  unitPercentage: string
  flatFee: string
}
export type StandardPrice = BasePrice<
  'STANDARD',
  {
    price: string
    billingType: BillingType
    billingFrequency: BillingFrequency
  }
>
export type LinearPrice = BasePrice<
  'LINEAR',
  {
    price: string
    percentage: string
    usageMetricId: string
    linearPriceType: LinearPriceType
    billingFrequency: BillingFrequency
    billingType: 'IN_ARREARS'
    minPrice: string
    maxPrice: string
    parameters: Record<string, string>
  }
>
export type GraduatedPrice = BasePrice<
  'GRADUATED',
  {
    usageMetricId: string
    tiers: PriceEditorTier[]
    percentageTiers: PriceEditorPercentageTier[]
    usageTierType: UsageTierType
    usageCalculationMode: UsageCalculationMode
    usageCalculationPeriod?: {
      frequency: UsageCalculationFrequency
      interval: number
    }
    billingFrequency: BillingFrequency
    billingType: 'IN_ARREARS'
    parameters: Record<string, string>
  }
>
export type VolumePrice = BasePrice<
  'VOLUME',
  {
    usageMetricId: string
    tiers: PriceEditorTier[]
    percentageTiers: PriceEditorPercentageTier[]
    usageTierType: UsageTierType
    billingFrequency: BillingFrequency
    billingType: 'IN_ARREARS'
    includePercentageLimits: boolean
    parameters: Record<string, string>
  }
>
export type PackagedPrice = BasePrice<
  'PACKAGE',
  {
    usageMetricId: string
    pricePerPackage: string
    packageSize: string
    billingFrequency: BillingFrequency
    billingType: 'IN_ARREARS'
    parameters: Record<string, string>
  }
>
export type SeatOveragesBillingFrequency =
  | 'NEVER'
  | 'NONE'
  | Exclude<BillingFrequency, 'PER_EVENT'>

export type SeatBasedProrationStrategy = ProrationStrategy
export type SeatBasedLinearPrice = BasePrice<
  'SEAT_BASED_LINEAR',
  {
    seatMetricId: string
    pricePerSeat: string
    contractedMinimumSeats: string
    prorationStrategy: SeatBasedProrationStrategy
    overagesBillingFrequency?: SeatOveragesBillingFrequency
    billingFrequency: BillingFrequency
    billingType: BillingType
    prorateFlatFees: boolean
  }
>
export type SeatBasedGraduatedPrice = BasePrice<
  'SEAT_BASED_GRADUATED',
  {
    seatMetricId: string
    pricePerSeat: string
    contractedMinimumSeats: string
    prorationStrategy: SeatBasedProrationStrategy
    overagesBillingFrequency?: SeatOveragesBillingFrequency
    billingFrequency: BillingFrequency
    billingType: BillingType
    tiers: PriceEditorTier[]
    prorateFlatFees: boolean
  }
>

export type Price =
  | StandardPrice
  | LinearPrice
  | GraduatedPrice
  | VolumePrice
  | PackagedPrice
  | SeatBasedGraduatedPrice
  | SeatBasedLinearPrice

type BreadcrumbStep = {
  name: string
  mode: EditorMode
}

export enum EditorMode {
  ADD_PRODUCT = 'ADD_PRODUCT',
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  REVIEW = 'REVIEW'
}

/**
 * Internal types for domain implementation
 */
export type PricingEditorReducerState = {
  data: {
    pricingEditorData: {
      common: {
        id: string
        productId: string
        listPriceId?: string
        name: string
        pricingModel: PricingModel
        currency: Currency
        integrationIds: Record<PricingEditorLedgerIntegration['id'], string>
      }
      STANDARD: StandardPrice['modelSpecific']
      LINEAR: LinearPrice['modelSpecific']
      VOLUME: VolumePrice['modelSpecific']
      GRADUATED: GraduatedPrice['modelSpecific']
      PACKAGE: PackagedPrice['modelSpecific']
      SEAT_BASED_LINEAR: SeatBasedLinearPrice['modelSpecific']
      SEAT_BASED_GRADUATED: SeatBasedGraduatedPrice['modelSpecific']
    }
    integrations: Record<string, PricingEditorLedgerIntegration>
    listPrices: Record<string, Price>
    product: Product
  }
  configuration: {
    availableCurrencies: Currency[]
    currency?: Currency
    mode: EditorMode
    priceType: PricingEditorVariant
    productIdsUsedInPhase: string[]
    showProductSelectField: boolean
  }
  editor: {
    loaded: boolean
    showValidationErrors: boolean
    steps: BreadcrumbStep[]
    valid: boolean
    /**
     * The external ledgers are a sub-form, because of their relatively complexity
     * and that they conditionally appear on all forms. Therefore we need to track
     * validity of this sub-form as well as the main 'valid' state.
     */
    externalLedgersValid: boolean
    /**
     * Whether the fields tucked away behind the "Show additional fields" toggle
     * all pass validation. Tracked separately from `valid`, like the ledgers
     * above, so saving can open the section when the problem is down there.
     */
    additionalFieldsValid: boolean
    /**
     * We control this here, as otherwise it's really annoying for the
     * fields to keep collapsing when changing pricing model. The pricing
     * model field exacerbates this by being part of said fields.
     */
    showAdditionalFields: boolean
  }
  queries: {
    availableFeatures: {
      common: {
        breadcrumb: VisibleEnabledFeatureAvailable
        form: VisibleEnabledFeatureAvailable
        review: VisibleEnabledFeatureAvailable
        save: VisibleEnabledFeatureAvailable
      }
      listPrice: {
        addProduct: VisibleEnabledFeatureAvailable
      }
      variantPrice: {
        canChangeListPrice: VisibleEnabledFeatureAvailable
      }
    }
    breadcrumbSteps: {
      name: string
      mode: EditorMode
      active: boolean
    }[]
    formattedPricingEditorData: string
  }
  initialData: {
    mode: EditorMode
    price: Price
    product?: Product
  }
}

type Action<T extends string, P> = {
  type: T
  payload: P
}

export type LoadAction = Action<
  'load',
  Pick<PricingEditorReducerState, 'data' | 'configuration' | 'initialData'>
>

export type UpdateEditorAction = Action<
  'updateEditor',
  Partial<PricingEditorReducerState['editor']>
>

export type UpdateListPriceAction = Action<'updateListPrice', string>

export type UpdatePricingModelAction = Action<
  'updatePricingModel',
  PricingModel
>

export type UpdateConfigurationAction = Action<
  'updateConfiguration',
  Partial<PricingEditorReducerState['configuration']>
>

/**
 * We can't update the pricingModel via this action, since it is quite an important
 * property! updatePricingModel should be used instead.
 */
export type UpdatePricingEditorData = {
  common: Omit<
    PricingEditorReducerState['data']['pricingEditorData']['common'],
    'pricingModel' | 'listPriceId'
  >
} & Omit<PricingEditorReducerState['data']['pricingEditorData'], 'common'>

export type UpdatePricingEditorDataAction = Action<
  'updatePricingEditorData',
  RecursivePartial<UpdatePricingEditorData>
>

export type UpdateProductAction = Action<
  'updateProduct',
  Partial<PricingEditorReducerState['data']['product']>
>

export type PricingEditorActions =
  | LoadAction
  | UpdateEditorAction
  | UpdatePricingEditorDataAction
  | UpdateConfigurationAction
  | UpdateProductAction
  | UpdateListPriceAction
  | UpdatePricingModelAction

export type ActionHandler<A extends PricingEditorActions> = (
  prevState: PricingEditorReducerState
) => (action: A) => PricingEditorReducerState

export type PostActionContext = {
  preActionState: PricingEditorReducerState
  action: PricingEditorActions
}
export type PostActionStage = (
  ctx: PostActionContext
) => (prevState: PricingEditorReducerState) => PricingEditorReducerState

export type VisibleEnabledFeatureAvailable = {
  available: {
    visible: boolean
    enabled: boolean
  }
}

/**
 * Domain interface
 */
export type PricingEditorDomainInterface = {
  queries: PricingEditorReducerState['queries'] & {
    rawData: Pick<
      PricingEditorReducerState,
      'data' | 'configuration' | 'editor' | 'initialData'
    >
  }
  mutators: {
    external: {
      in: {
        load: () => Promise<{
          data: Pick<PricingEditorReducerState, 'data' | 'configuration'> | null
          error: PricingEditorPortErrors | null
        }>
      }
      out: {
        save: () => Promise<boolean>
      }
    }
    common: {
      updateEditor: (
        editorSettings: Partial<PricingEditorReducerState['editor']>
      ) => void
      updateConfiguration: (
        configuration: Partial<PricingEditorReducerState['configuration']>
      ) => void
      updatePricingEditorData: (
        newData: RecursivePartial<UpdatePricingEditorData>
      ) => void
      updateListPrice: (listPriceId: string) => void
      updatePricingModel: (pricingModel: PricingModel) => void
      updateProduct: (
        newProduct: Partial<PricingEditorReducerState['data']['product']>
      ) => void
    }
  }
}

/**
 * Port implementation type
 */
export type PricingEditorDomainOutput = PricingEditorDomainInterface['queries']
export type PricingEditorDomainInput = Pick<
  PricingEditorReducerState,
  'data' | 'configuration' | 'initialData'
>

export enum PricingEditorPortErrors {
  NotFound = 'notFound',
  ErrorOnProductSave = 'errorOnProductSave',
  ErrorOnPriceSave = 'errorOnPriceSave',
  ErrorOnListPriceSave = 'errorOnListPriceSave',
  ErrorOnListPriceSaveButProductCreated = 'errorOnListPriceSaveButProductCreated',
  Other = 'other'
}

export type PricingEditorPortImplementation = {
  in: {
    load: () => Promise<{
      data: PricingEditorDomainInput | null
      error: PricingEditorPortErrors | null
    }>
  }
  out: {
    save: (data: PricingEditorDomainOutput) => Promise<{
      success: boolean
      error: PricingEditorPortErrors | null
      data: {
        price: v1ApiCreateListPriceResult
        product?: v1ApiCreateProductResult
      }
    }>
  }
}

export type PricingEditorVariant = 'list' | 'variant'
